import React, { useState, useEffect, useMemo, useCallback } from 'react'
import classNames from 'classnames'
import shortid from 'shortid'
import { useSelector, useDispatch } from 'react-redux'
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc'

import styles from '../component-styles/links-table.module.scss'
import ToolTip from './tooltip'
import { GraphQLState } from '../store/graphql-reducer'
import { logActionAsync } from '../store/actions'

import { messages } from '../core/variables'
import Input from './input'
import Pagination from './pagination'
import { formatValueForDisplay, getItemByKeyValue } from '../helpers'
import { SelectBoxSimple } from './select-box'
import useTableSortFilter from '../hooks/useTableSortFilter'
import Button from './button'
import OrderArrow from './order-arrow'
import { saveUserData, getUserData } from '../local-client/local-client'
import { Preloader } from './loader'

interface ElementProps {
  currentMetricID: string
  setSortOrder: any
  sortKey: string
  orderAsc: boolean
  item: any
}
export function ThElement({
  currentMetricID,
  setSortOrder,
  sortKey,
  orderAsc,
  item,
}: ElementProps): React.ReactElement {
  return (
    <>
      <Button
        className={classNames(styles.headerButton, {
          [styles.hasOrderArrow]: true,
        })}
        type="none"
      >
        {item.metricName}
      </Button>
      <OrderArrow
        className={styles.orderArrow}
        currentKey={item.metricID}
        sortKey={sortKey}
        orderAsc={orderAsc}
      />
      <ToolTip
        showOnLeft
        showUnder
        className={styles.headerTooltip}
        message={item.helpText}
      />
    </>
  )
}

const SortableItem = SortableElement(
  ({
    currentMetricID,
    setSortOrder,
    sortKey,
    orderAsc,
    item,
  }: ElementProps) => {
    return (
      <th
        className={classNames(styles.headerCell, {
          [styles.highlight]: currentMetricID === item.metricID,
        })}
        onClick={() => {
          setSortOrder(item.metricID)
        }}
      >
        <ThElement
          currentMetricID={currentMetricID}
          setSortOrder={setSortOrder}
          sortKey={sortKey}
          orderAsc={orderAsc}
          item={item}
        />
      </th>
    )
  },
)

const SortableWrapper = SortableContainer(
  ({
    children,
    useColumnKeys,
    showColumns,
    loading,
    currentMetricID,
    setSortOrder,
    sortKey,
    orderAsc,
  }): React.ReactElement => {
    return (
      <tr>
        {children}
        {useColumnKeys.map((item, index) => {
          if (!item.show) {
            return null
          }
          // Limit the colums to show
          if (
            showColumns.length > 0 &&
            showColumns.indexOf(item.metricID) === -1
          ) {
            return null
          }
          if (loading) {
            return (
              <th
                key={shortid.generate()}
                className={classNames(styles.headerCell)}
              >
                {item.metricName}
                <ToolTip
                  showOnLeft
                  showUnder
                  className={styles.headerTooltip}
                  message={item.helpText}
                />
              </th>
            )
          }
          if (index === 0) {
            return (
              <th
                key={shortid.generate()}
                className={classNames(styles.headerCell, {
                  [styles.highlight]: currentMetricID === item.metricID,
                })}
                onClick={() => {
                  setSortOrder(item.metricID)
                }}
              >
                <ThElement
                  currentMetricID={currentMetricID}
                  setSortOrder={setSortOrder}
                  sortKey={sortKey}
                  orderAsc={orderAsc}
                  item={item}
                />
              </th>
            )
          }

          return (
            <SortableItem
              index={index}
              key={shortid.generate()}
              currentMetricID={currentMetricID}
              setSortOrder={setSortOrder}
              sortKey={sortKey}
              orderAsc={orderAsc}
              item={item}
            />
          )
        })}
      </tr>
    )
  },
)

export function ValueNotSet(): React.ReactElement {
  return (
    <div>
      <ToolTip showOnRight className={styles.tooltip} message={messages.notSet}>
        <div className={styles.row}>
          <span className={styles.notSet}>(not set)</span>
          <span className={classNames(styles.hint, styles.hintPartial)}>!</span>
        </div>
      </ToolTip>
    </div>
  )
}

interface TableLinkProps {
  campaignLink: string
  separator?: string
  prefix?: string
  active?: boolean
  showHighlight?: boolean
  className?: string
}

export function TableLink({
  className,
  campaignLink,
  separator = '&',
  prefix = '?',
  active = false,
  showHighlight,
}: TableLinkProps): React.ReactElement {
  const searchRegEx = new RegExp(
    `([^=${separator}]+)(=([^${separator}]*))?`,
    'gi',
  )

  const splitLink = campaignLink.split(prefix)
  const useCampaignLink =
    splitLink.length === 1 ? splitLink[0] : splitLink.slice(1).join(prefix)
  const found = useCampaignLink.match(searchRegEx)
  const useHighlight =
    found !== null &&
    (found.length > 1 || (found.length === 1 && splitLink.length > 1))

  return (
    <span className={classNames(styles.tableLinkWrapper, className)}>
      <>{`${splitLink.length > 1 ? splitLink[0] + prefix : ''}`}</>
      <span
        className={classNames({
          [styles.elementActive]: active,
        })}
      >
        {useHighlight &&
          found &&
          found.map((foundQueryValueString, partsIndex) => {
            const parts = foundQueryValueString.split('=')
            return parts.map((l, indx) => {
              const renderLink = `${
                indx === 0 && partsIndex > 0 ? separator : ''
              }${l}${parts.length > 1 && indx === 0 ? '=' : ''}`

              return (
                <span
                  key={shortid.generate()}
                  className={classNames(styles.tableLink, {
                    [styles.codeHighlighted]: indx > 0 && showHighlight,
                  })}
                >
                  {renderLink}
                </span>
              )
            })
          })}
        {!useHighlight && <>{useCampaignLink}</>}
      </span>
    </span>
  )
}

// columnKeys: {
//   show: boolean
//   helpText: string
//   metricName: string
//   metricID: string
//   units: 'float' | 'percentage' | 'duration'
// }[]

interface LinksTableProps {
  loading: boolean
  currentMetricID?: string
  showHighlight?: boolean
  updateShowHighlight: () => any
  children: any
  reverse: boolean
  showColumns: string[]
  matchType: string
  showHighlightInput?: boolean
}

export default function LinksTable({
  loading,
  currentMetricID,
  showHighlight,
  updateShowHighlight,
  children,
  reverse,
  showColumns,
  matchType,
  showHighlightInput = false,
}: LinksTableProps): React.ReactElement {
  const [useData, setUseData] = useState<any[]>([])
  const { graphql }: { graphql: GraphQLState } = useSelector((s: any): any => s)

  const {
    campaignCodeGenerator,
    campaignLinkDashboardTableRows,
    currentAccount,
  } = graphql

  const [useColumnKeys, setUseColumnKeys] = useState<any[]>([])

  useEffect(() => {
    if (
      currentAccount &&
      currentAccount.accountID &&
      useColumnKeys.length > 0
    ) {
      saveUserData({ useColumnKeys }, currentAccount.accountID)
    }
  }, [useColumnKeys])

  const dispatch = useDispatch()

  const { tableData, columnKeys } = campaignLinkDashboardTableRows || {
    tableData: [],
    columnKeys: [],
  }

  const { separator, prefix } = useMemo(() => {
    if (campaignCodeGenerator) {
      const { paramSeparator, masterPrefix } = campaignCodeGenerator
      return { separator: paramSeparator, prefix: masterPrefix }
    }
    return { separator: '&', prefix: '?' }
  }, [campaignCodeGenerator])

  // const tableData = items ? [...items] : []

  const {
    rowsPerPage,
    setRowsPerPage,
    orderAsc,
    sortKey,
    setSortOrder,
    pages,
    activePage,
    setActivePage,
    orderedList,
    setToBeSorted,
  } = useTableSortFilter(useData, '', '', 10)

  useEffect((): void => {
    if (
      tableData &&
      Array.isArray(tableData) &&
      tableData.length > 0 &&
      columnKeys &&
      Array.isArray(columnKeys) &&
      columnKeys.length > 0 &&
      currentAccount &&
      currentAccount.accountID
    ) {
      const data = [...tableData]
      if (reverse) {
        data.reverse()
      }

      const savedData = getUserData(currentAccount.accountID)
      const hasSavedData =
        savedData &&
        Object.prototype.hasOwnProperty.call(savedData, 'useColumnKeys') &&
        Array.isArray(savedData.useColumnKeys) &&
        savedData.useColumnKeys.length > 0
      const successMetricHasNotChanged =
        hasSavedData && savedData.useColumnKeys[0].metricID === currentMetricID
      if (successMetricHasNotChanged) {
        setUseColumnKeys(savedData.useColumnKeys)
      } else {
        const foundIndex = getItemByKeyValue(
          columnKeys,
          'metricID',
          currentMetricID,
          true,
        )
        let colIndex = columnKeys.map((item, index) => {
          return index
        })
        if (foundIndex > 0) {
          // Reorder columns
          colIndex = [...colIndex.splice(foundIndex, 1), ...colIndex]
        }
        const columnKeysData: any[] = []
        colIndex.forEach((index) => {
          columnKeysData.push(columnKeys[index])
        })
        setUseColumnKeys(columnKeysData)
      }
      setUseData(data)
      setSortOrder('')
      setToBeSorted(false)
    }
  }, [tableData, currentMetricID, columnKeys, reverse, currentAccount])

  const { headerColSpan, showSuccessCol, showHeaders } = useMemo(() => {
    const hasSuccessCol =
      // showColumns.length === 0 ||
      showColumns.indexOf(currentMetricID || '') !== -1
    const use = useColumnKeys.filter((item) => {
      return !(
        (!item.show || showColumns.indexOf(item.metricID) === -1)
        // (showColumns.length > 0 && showColumns.indexOf(item.metricID) === -1)
      )
    })
    const span = use.length - (hasSuccessCol ? 1 : 0)
    return {
      showHeaders: span > 1,
      headerColSpan: span,
      showSuccessCol: hasSuccessCol,
    }
  }, [useColumnKeys, showColumns, currentMetricID])

  const mainTh = useCallback(() => {
    return (
      <>
        {children}
        {showHighlightInput &&
          (matchType === 'full' || matchType === 'partial') && (
            <div className={styles.highlightCheckboxContainer}>
              <Input
                label="Highlight values"
                type="checkbox"
                name="Highlight values"
                checked={showHighlight}
                className={styles.highlightCheckbox}
                onChange={() => {
                  updateShowHighlight()
                  const actionData = {
                    action: 'highlight-values-changed',
                    longDescription: `${showHighlight ? 'hide' : 'show'}`,
                    websiteSection: 'report',
                  }
                  dispatch(logActionAsync(actionData))
                }}
              />
            </div>
          )}
      </>
    )
  }, [showHighlight, children, matchType])

  if (loading) {
    return (
      <>
        <div
          className={classNames(
            styles.tableContainer,
            styles.tableContainerLoading,
          )}
        >
          <table className={classNames(styles.table, styles.linksTable)}>
            <tbody className={classNames(styles.tableBody, styles.emptyData)}>
              <tr>
                <td colSpan={tableData.length + 1}>
                  <div
                    style={{ height: 400, paddingTop: 175, paddingBottom: 175 }}
                  >
                    <Preloader
                      style={{ width: 60, height: 50, margin: '0 auto' }}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    )
  }
  if (!tableData || !columnKeys) {
    return <></>
  }
  return (
    <>
      <div className={styles.tableContainer}>
        <table className={classNames(styles.table, styles.linksTable)}>
          <thead>
            {showHeaders && (
              <tr>
                <th
                  rowSpan={2}
                  className={classNames(styles.linksMainHeaderCell, {
                    [styles.fullMatch]:
                      matchType === 'full' || matchType === 'partial',
                  })}
                >
                  {mainTh()}
                </th>
                {showSuccessCol && (
                  <th
                    className={classNames(
                      styles.headerTh,
                      styles.successMetric,
                    )}
                  >
                    Success metric
                  </th>
                )}
                {headerColSpan > 0 && (
                  <th
                    colSpan={headerColSpan}
                    className={classNames(
                      styles.headerTh,
                      styles.supportMetric,
                    )}
                  >
                    Support metrics
                  </th>
                )}
              </tr>
            )}
            <SortableWrapper
              loading={loading}
              currentMetricID={currentMetricID}
              useColumnKeys={useColumnKeys}
              showColumns={showColumns}
              helperClass={styles.itemDragged}
              setSortOrder={setSortOrder}
              sortKey={sortKey}
              orderAsc={orderAsc}
              lockToContainerEdges
              axis="x"
              lockAxis="x"
              // pressDelay={600}
              distance={5}
              // onClick={()=>{

              // }}
              onSortOver={(vals) => {
                const { oldIndex, newIndex } = vals
                if (oldIndex !== newIndex) {
                  const orderedArray = arrayMove(
                    useColumnKeys,
                    oldIndex,
                    newIndex,
                  )
                  setUseColumnKeys(orderedArray)
                }
              }}
            >
              <>
                {!showHeaders && (
                  <th
                    className={classNames(styles.linksMainHeaderCell, {
                      [styles.fullMatch]:
                        matchType === 'full' || matchType === 'partial',
                    })}
                  >
                    {mainTh()}
                  </th>
                )}
              </>
            </SortableWrapper>
          </thead>
          {!loading && orderedList.length === 0 && (
            <tbody className={classNames(styles.tableBody, styles.emptyData)}>
              <tr>
                <td colSpan={tableData.length + 1}>
                  <div>
                    <b />
                    <p>We have no data for your selected filters.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          )}
          {!loading && orderedList.length > 0 && (
            <tbody>
              {orderedList[
                orderedList.length < activePage - 1 ? 0 : activePage - 1
              ].map((row) => {
                const { cL: campaignLink } = row
                const rowHasValue = useColumnKeys.some(({ metricID }) => {
                  return row[metricID] > 0
                })
                return (
                  <tr key={shortid.generate()}>
                    <th className={styles.tdCode}>
                      {campaignLink === '' ? (
                        <ValueNotSet />
                      ) : (
                        <TableLink
                          prefix={prefix}
                          separator={separator}
                          campaignLink={campaignLink}
                          showHighlight={showHighlight}
                        />
                      )}
                    </th>
                    {useColumnKeys.map(({ units, metricID, show }) => {
                      if (!show) {
                        return null
                      }
                      // Limit the colums to show
                      if (
                        showColumns.length > 0 &&
                        showColumns.indexOf(metricID) === -1
                      ) {
                        return null
                      }
                      const useValue = row[metricID]
                      const displayValue = formatValueForDisplay(
                        useValue,
                        units,
                        !rowHasValue,
                        units === 'percentage', // hide -1 ones
                      )

                      return (
                        <td
                          className={classNames(styles.valueCell, {
                            [styles.highlight]: currentMetricID === metricID,
                          })}
                          key={shortid.generate()}
                        >
                          {displayValue}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          )}
        </table>
      </div>
      {!loading && (
        <div className={styles.paginationRow}>
          <Pagination
            className={styles.pagination}
            pages={pages}
            activePage={activePage}
            onChange={(index: number) => {
              setActivePage(index)
            }}
          />
          <span className={styles.rowsLabelWrapper}>
            <span className={styles.rowsLabel}>Show</span>
            <SelectBoxSimple
              name="rows-per-page"
              value={rowsPerPage}
              onChange={(rows: number) => {
                setRowsPerPage(rows)
                setActivePage(1)
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
            </SelectBoxSimple>
          </span>
        </div>
      )}
    </>
  )
}
